import { useState, useEffect, useMemo } from 'react';
import PageHeader from '../components/ui/page-header';
import { HelpButton } from '../components/ui/help-button';
import { FeedbackButton } from '../components/ui/feedback-button';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { KpiStrip } from '../components/ui/kpi-strip';
import { RightPanel } from '../components/ui/right-panel';
import { ChainVisual } from '../components/ui/chain-visual';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Crosshair, ExternalLink, RefreshCw, Loader2, Search, AlertTriangle,
  Shield, Database, Bug, Lock, Clock, Filter, Target, Link2, ArrowRight,
  Package, TrendingUp, Activity, Zap, Wifi, Globe, Eye, Users, ChevronDown, ChevronUp, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { ContextualTip } from '../components/ContextualTip';
import { Link, useNavigate } from 'react-router-dom';
import AuditLogPanel from '../components/AuditLogPanel';
import DecisionRow from '../components/DecisionRow';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ReferenceLine, LineChart, Line
} from 'recharts';

const CATEGORY_CONFIG = {
  ransomware: { label: 'Ransomware', icon: Lock, color: 'bg-red-50 text-red-700 border-red-200' },
  datalek: { label: 'Datalek', icon: Database, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  kwetsbaarheid: { label: 'Kwetsbaarheid', icon: AlertTriangle, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  malware: { label: 'Malware', icon: Bug, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  update: { label: 'Update', icon: RefreshCw, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  privacy: { label: 'Privacy', icon: Shield, color: 'bg-green-50 text-green-700 border-green-200' },
  algemeen: { label: 'Algemeen', icon: Crosshair, color: 'bg-neutral-50 text-neutral-700 border-neutral-200' }
};

const SEVERITY_CONFIG = {
  critical: { label: 'Kritiek', color: 'bg-red-600 text-white' },
  high: { label: 'Hoog', color: 'bg-orange-500 text-white' },
  medium: { label: 'Medium', color: 'bg-yellow-500 text-white' },
  info: { label: 'Info', color: 'bg-blue-500 text-white' }
};

const BUBBLE_THREATS = [
  { name: 'Ransomware', intent: 90, capability: 85, impact: 95, fill: '#b91c1c', cat: 'Malware' },
  { name: 'Phishing', intent: 85, capability: 60, impact: 75, fill: '#ef4444', cat: 'Social Engineering' },
  { name: 'Supply Chain', intent: 70, capability: 75, impact: 70, fill: '#f97316', cat: 'Indirect' },
  { name: 'Zero-day', intent: 0, capability: 95, impact: 90, fill: '#8b5cf6', cat: 'Exploits' },
  { name: 'DDoS', intent: 75, capability: 45, impact: 40, fill: '#3b82f6', cat: 'Beschikbaarheid' },
  { name: 'Insider', intent: 45, capability: 55, impact: 50, fill: '#eab308', cat: 'Intern' },
  { name: 'Credential Stuffing', intent: 80, capability: 50, impact: 55, fill: '#dc2626', cat: 'Toegang' },
  { name: 'Cryptojacking', intent: 65, capability: 40, impact: 25, fill: '#059669', cat: 'Malware' },
  { name: 'IoT Exploits', intent: 55, capability: 70, impact: 60, fill: '#0891b2', cat: 'Exploits' },
  { name: 'Social Engineering', intent: 80, capability: 35, impact: 65, fill: '#e11d48', cat: 'Social Engineering' },
  { name: 'MitM', intent: 50, capability: 65, impact: 55, fill: '#7c3aed', cat: 'Netwerk' },
  { name: 'API Abuse', intent: 70, capability: 60, impact: 45, fill: '#0d9488', cat: 'Applicatie' },
  { name: 'Watering Hole', intent: 40, capability: 80, impact: 65, fill: '#a855f7', cat: 'Indirect' },
  { name: 'DNS Spoofing', intent: 55, capability: 50, impact: 35, fill: '#6366f1', cat: 'Netwerk' },
  { name: 'Deepfake', intent: 35, capability: 75, impact: 70, fill: '#ec4899', cat: 'Social Engineering' },
];

// Top 5 actors per category with impact & recommended action
const CATEGORY_ACTORS = {
  'Malware': [
    { name: 'LockBit 3.0', impact: 'Versleutelt bedrijfsdata en eist losgeld tot €500K+', action: 'Implementeer offline backups, EDR en netwerksegmentatie', severity: 'Kritiek' },
    { name: 'BlackCat/ALPHV', impact: 'Dubbele afpersing: data-exfiltratie + encryptie', action: 'Zero trust architectuur en data loss prevention (DLP)', severity: 'Kritiek' },
    { name: 'Emotet', impact: 'Verspreidt zich via e-mail, installeert banking trojans', action: 'E-mailfiltering en endpoint protection versterken', severity: 'Hoog' },
    { name: 'QakBot', impact: 'Credential theft en laterale beweging in netwerk', action: 'MFA op alle accounts, netwerk monitoring', severity: 'Hoog' },
    { name: 'RedLine Stealer', impact: 'Steelt browser-wachtwoorden en cryptocurrency wallets', action: 'Wachtwoordmanager verplichten, browser hardening', severity: 'Medium' },
  ],
  'Social Engineering': [
    { name: 'Scattered Spider', impact: 'Social engineering op IT-helpdesk voor initiële toegang', action: 'Verificatieprotocollen voor helpdesk, awareness training', severity: 'Kritiek' },
    { name: 'FIN7', impact: 'Gerichte spear-phishing op financiële afdelingen', action: 'Betalingsverificatie protocol, DMARC/SPF/DKIM', severity: 'Kritiek' },
    { name: 'Lazarus Group', impact: 'Geavanceerde phishing-campagnes gericht op crypto/tech', action: 'Threat intelligence feeds, geavanceerde e-mailfiltering', severity: 'Hoog' },
    { name: 'TA505', impact: 'Massale spam-campagnes met malware-bijlagen', action: 'Bijlage-sandboxing, macro-beleid verscherpen', severity: 'Hoog' },
    { name: 'Vishing groups', impact: 'Telefonische fraude namens banken/overheid', action: 'Bewustzijnstraining, out-of-band verificatie', severity: 'Medium' },
  ],
  'Exploits': [
    { name: 'APT29 (Cozy Bear)', impact: 'Exploiteert zero-days in VPN en e-mailservers', action: 'Patch management versnellen, virtual patching via WAF', severity: 'Kritiek' },
    { name: 'APT28 (Fancy Bear)', impact: 'Zero-day exploits in Microsoft en Cisco producten', action: 'Vulnerability scanning, minimale aanvalsoppervlakte', severity: 'Kritiek' },
    { name: 'Exploit brokers', impact: 'Verkoop van onbekende kwetsbaarheden op zwarte markt', action: 'Bug bounty programma, threat intelligence abonnement', severity: 'Hoog' },
    { name: 'Cl0p', impact: 'Massale exploitatie van file-transfer kwetsbaarheden', action: 'Alternatieve file-transfer methoden, netwerksegmentatie', severity: 'Hoog' },
    { name: 'Script kiddies', impact: 'Automatische exploitatie van bekende kwetsbaarheden', action: 'Reguliere patching, IDS/IPS configureren', severity: 'Medium' },
  ],
  'Indirect': [
    { name: 'SolarWinds-type', impact: 'Supply chain compromitatie via software-updates', action: 'SBOM bijhouden, leveranciers-assessments verplichten', severity: 'Kritiek' },
    { name: 'Cloud providers', impact: 'Gedeeld verantwoordelijkheidsmodel, misconfiguratie risico', action: 'Cloud security posture management (CSPM)', severity: 'Hoog' },
    { name: 'MSP/MSSP compromitatie', impact: 'Aanvallers gebruiken managed service providers als springplank', action: 'MSP toegang beperken, monitoring van MSP-activiteiten', severity: 'Hoog' },
    { name: 'Open source dependencies', impact: 'Malicious packages in npm/PyPI ecosysteem', action: 'Software composition analysis (SCA), dependency scanning', severity: 'Medium' },
    { name: 'Watering hole operators', impact: 'Infecteren van sector-specifieke websites', action: 'Web isolation, browser hardening, DNS filtering', severity: 'Medium' },
  ],
  'Beschikbaarheid': [
    { name: 'Anonymous Sudan', impact: 'DDoS-aanvallen op overheid en financiële sector', action: 'Anti-DDoS dienst, CDN, rate limiting implementeren', severity: 'Hoog' },
    { name: 'Killnet', impact: 'Politiek gemotiveerde DDoS op westerse doelen', action: 'Redundante infrastructuur, DDoS mitigatieplan', severity: 'Hoog' },
    { name: 'DDoS-for-hire', impact: 'Laagdrempelige DDoS services voor €10/aanval', action: 'Basis DDoS-bescherming op alle publieke diensten', severity: 'Medium' },
    { name: 'Botnet operators', impact: 'IoT-botnets voor volumetrische aanvallen', action: 'IoT-segmentatie, upstream filtering', severity: 'Medium' },
    { name: 'Ransom DDoS', impact: 'DDoS-dreigement met losgeld-eis', action: 'Niet betalen, incident response plan activeren', severity: 'Medium' },
  ],
  'Netwerk': [
    { name: 'APT groups', impact: 'Man-in-the-middle aanvallen op bedrijfsnetwerken', action: 'Encryptie op alle verbindingen, certificate pinning', severity: 'Kritiek' },
    { name: 'Rogue access points', impact: 'Nep WiFi-netwerken voor credential harvesting', action: 'Wireless intrusion detection, VPN-verplichting', severity: 'Hoog' },
    { name: 'BGP hijackers', impact: 'Routering van verkeer via malicious netwerken', action: 'RPKI implementeren, monitoring van BGP-routes', severity: 'Hoog' },
    { name: 'DNS poisoners', impact: 'Omleiden van verkeer naar phishing-sites', action: 'DNSSEC implementeren, DNS monitoring', severity: 'Medium' },
    { name: 'Packet sniffers', impact: 'Afluisteren van onversleuteld netwerkverkeer', action: 'TLS everywhere, netwerksegmentatie', severity: 'Medium' },
  ],
  'Toegang': [
    { name: 'Initial Access Brokers', impact: 'Verkopen bedrijfstoegangen voor €500-€50K op dark web', action: 'Dark web monitoring, credential leak detectie', severity: 'Kritiek' },
    { name: 'Credential stuffers', impact: 'Automatische aanvallen met gelekte wachtwoorden', action: 'MFA verplicht, wachtwoord-hergebruik detectie', severity: 'Hoog' },
    { name: 'Brute force bots', impact: 'Geautomatiseerd wachtwoorden raden', action: 'Account lockout beleid, CAPTCHA, rate limiting', severity: 'Hoog' },
    { name: 'Pass-the-hash', impact: 'Laterale beweging met gestolen authenticatietokens', action: 'Privileged access management, credential guard', severity: 'Medium' },
    { name: 'Session hijackers', impact: 'Overnemen van actieve sessies via token theft', action: 'Korte sessie-timeouts, IP-binding, secure cookies', severity: 'Medium' },
  ],
  'Intern': [
    { name: 'Ontevreden medewerkers', impact: 'Data-exfiltratie, sabotage van systemen', action: 'DLP-beleid, least privilege, exit-procedures', severity: 'Hoog' },
    { name: 'Nalatige gebruikers', impact: 'Onbedoelde blootstelling van gevoelige data', action: 'Security awareness, data classificatie, encryptie', severity: 'Hoog' },
    { name: 'Shadow IT', impact: 'Ongeautoriseerde cloud-diensten zonder beveiliging', action: 'Cloud access security broker (CASB), IT-beleid', severity: 'Medium' },
    { name: 'Contractors', impact: 'Externe partijen met te brede toegang', action: 'Tijdelijke accounts, beperkte rechten, audit trail', severity: 'Medium' },
    { name: 'Social media lekken', impact: 'Medewerkers delen gevoelige info op LinkedIn/social', action: 'Social media beleid, awareness over OSINT-risico', severity: 'Laag' },
  ],
  'Applicatie': [
    { name: 'OWASP Top 10 exploiters', impact: 'SQL injection, XSS, broken authentication', action: 'Secure coding training, SAST/DAST tools', severity: 'Kritiek' },
    { name: 'API abusers', impact: 'Misbruik van onbeveiligde API-endpoints', action: 'API gateway, rate limiting, input validatie', severity: 'Hoog' },
    { name: 'Web scrapers', impact: 'Geautomatiseerd verzamelen van bedrijfsdata', action: 'Bot detection, CAPTCHA, WAF-regels', severity: 'Medium' },
    { name: 'Formjacking groups', impact: 'Injectie van skimming code in webformulieren', action: 'Content Security Policy, subresource integrity', severity: 'Hoog' },
    { name: 'Mobile app exploiters', impact: 'Reverse engineering van mobiele apps', action: 'Code obfuscation, certificate pinning, RASP', severity: 'Medium' },
  ],
};

const THREAT_TIMELINE_DATA = [
  // 2025 - Historisch
  { periode: 'Q1 2025', ransomware: 18, phishing: 32, supply_chain: 6, zero_day: 3, ddos: 12, insider: 8, complexiteit: 58, forecast: false },
  { periode: 'Q2 2025', ransomware: 22, phishing: 36, supply_chain: 8, zero_day: 4, ddos: 14, insider: 9, complexiteit: 62, forecast: false },
  { periode: 'Q3 2025', ransomware: 28, phishing: 40, supply_chain: 10, zero_day: 5, ddos: 11, insider: 10, complexiteit: 66, forecast: false },
  { periode: 'Q4 2025', ransomware: 34, phishing: 45, supply_chain: 12, zero_day: 7, ddos: 15, insider: 11, complexiteit: 71, forecast: false },
  // 2026 - Huidig
  { periode: 'Q1 2026', ransomware: 38, phishing: 48, supply_chain: 14, zero_day: 9, ddos: 13, insider: 12, complexiteit: 75, forecast: false },
  { periode: 'Q2 2026', ransomware: 42, phishing: 52, supply_chain: 16, zero_day: 11, ddos: 16, insider: 13, complexiteit: 78, forecast: true },
  // 2026-2027 - Voorspellingen
  { periode: 'Q3 2026', ransomware: 48, phishing: 56, supply_chain: 19, zero_day: 14, ddos: 18, insider: 14, complexiteit: 82, forecast: true },
  { periode: 'Q4 2026', ransomware: 54, phishing: 60, supply_chain: 22, zero_day: 17, ddos: 20, insider: 16, complexiteit: 85, forecast: true },
  { periode: 'Q1 2027', ransomware: 60, phishing: 65, supply_chain: 26, zero_day: 21, ddos: 22, insider: 18, complexiteit: 88, forecast: true },
  { periode: 'Q2 2027', ransomware: 68, phishing: 70, supply_chain: 30, zero_day: 26, ddos: 24, insider: 20, complexiteit: 92, forecast: true },
];

const THREAT_ANALYSIS = [
  { name: 'Ransomware', level: 'Kritiek', color: 'bg-red-600', cat: 'Malware', trend: 'up', desc: 'Versleutelt bestanden en eist losgeld. Steeds vaker gecombineerd met data-exfiltratie (dubbele afpersing).', voorbeeld: 'LockBit, BlackCat, Cl0p: gemiddelde schade €250.000+. In 2025 steeg ransomware met 40%.', tip: 'Offline backups, netwerksegmentatie, EDR, incident response plan.' },
  { name: 'Phishing', level: 'Kritiek', color: 'bg-red-500', cat: 'Social Engineering', trend: 'up', desc: '91% van cyberaanvallen start met phishing. Door AI worden e-mails steeds overtuigender.', voorbeeld: 'CEO-fraude, valse facturen, spear-phishing op financiele afdelingen.', tip: 'Security awareness training, DMARC/SPF/DKIM, MFA op alle accounts.' },
  { name: 'Zero-day Exploits', level: 'Kritiek', color: 'bg-purple-500', cat: 'Exploits', trend: 'up', desc: 'Onbekende kwetsbaarheden zonder beschikbare patch. Worden verhandeld op de zwarte markt.', voorbeeld: 'Log4Shell, MOVEit Transfer: massale exploitatie voordat patches beschikbaar waren.', tip: 'WAF, virtual patching, threat intelligence feeds, minimale aanvalsoppervlakte.' },
  { name: 'Supply Chain Aanval', level: 'Hoog', color: 'bg-orange-500', cat: 'Indirect', trend: 'up', desc: 'Aanvallers infiltreren via vertrouwde leveranciers. Moeilijk te detecteren door legitieme bronnen.', voorbeeld: 'SolarWinds Orion, Kaseya VSA, 3CX: malware via software-updates.', tip: 'Leveranciers-assessments, code signing verificatie, minimale rechten, SBOM.' },
  { name: 'Credential Stuffing', level: 'Hoog', color: 'bg-red-400', cat: 'Toegang', trend: 'stable', desc: 'Geautomatiseerd testen van gestolen wachtwoorden op meerdere diensten.', voorbeeld: 'Miljarden gelekte credentials van LinkedIn, Adobe. Worden hergebruikt op bedrijfssystemen.', tip: 'MFA verplicht, wachtwoordbeleid, credential monitoring (Have I Been Pwned).' },
  { name: 'Social Engineering', level: 'Hoog', color: 'bg-rose-500', cat: 'Social Engineering', trend: 'up', desc: 'Manipulatie van medewerkers om toegang of informatie te verkrijgen. Combineert psychologie met technologie.', voorbeeld: 'Vishing (telefonisch), pretexting, baiting met USB-sticks op parkeerplaatsen.', tip: 'Bewustzijnstraining, verificatieprotocollen, meldcultuur stimuleren.' },
  { name: 'DDoS', level: 'Hoog', color: 'bg-blue-500', cat: 'Beschikbaarheid', trend: 'stable', desc: 'Overbelasting van systemen. Vaak als afleiding voor gelijktijdige data-exfiltratie.', voorbeeld: 'Aanvallen op banken en overheid, volumetrisch tot 1Tbps, applicatielaag steeds geavanceerder.', tip: 'CDN, rate limiting, anti-DDoS diensten, redundante infrastructuur.' },
  { name: 'IoT Exploits', level: 'Hoog', color: 'bg-cyan-600', cat: 'Exploits', trend: 'up', desc: 'Onbeveiligde IoT-apparaten als springplank naar het netwerk. Standaard wachtwoorden zijn een groot risico.', voorbeeld: 'Mirai botnet, camera-hacks, slimme gebouwbeheer-systemen als toegangspoort.', tip: 'Netwerksegmentatie voor IoT, firmware updates, standaard credentials wijzigen.' },
  { name: 'Insider Dreiging', level: 'Medium', color: 'bg-yellow-500', cat: 'Intern', trend: 'stable', desc: 'Medewerkers met directe toegang die bewust of onbewust schade veroorzaken. 60% van datalekken is intern.', voorbeeld: 'Ontslagen werknemer kopieert klantdata, onbewust doorsturen van gevoelige documenten.', tip: 'Least privilege, DLP, exit-procedures, gedragsmonitoring, vertrouwenscultuur.' },
  { name: 'API Abuse', level: 'Medium', color: 'bg-teal-500', cat: 'Applicatie', trend: 'up', desc: 'Misbruik van slecht beveiligde API\'s. Ongeautoriseerde data-extractie via publieke endpoints.', voorbeeld: 'Scraping van klantdata via onbeveiligde REST API\'s, rate limit omzeiling.', tip: 'API gateway, rate limiting, OAuth2, input validatie, API inventarisatie.' },
  { name: 'Man-in-the-Middle', level: 'Medium', color: 'bg-violet-500', cat: 'Netwerk', trend: 'stable', desc: 'Onderscheppen van communicatie tussen twee partijen. Effectief op onbeveiligde netwerken.', voorbeeld: 'WiFi evil twin aanvallen, ARP spoofing op bedrijfsnetwerken.', tip: 'TLS overal, certificate pinning, VPN, HSTS, netwerk monitoring.' },
  { name: 'Watering Hole', level: 'Medium', color: 'bg-purple-400', cat: 'Indirect', trend: 'stable', desc: 'Infectie van websites die het doelwit regelmatig bezoekt. Zeer gericht en moeilijk te detecteren.', voorbeeld: 'Gecompromitteerde branche-websites, geinfecteerde software-download portals.', tip: 'Browserisolatie, endpoint detectie, website reputatie monitoring.' },
  { name: 'Deepfake', level: 'Medium', color: 'bg-pink-500', cat: 'Social Engineering', trend: 'up', desc: 'AI-gegenereerde video/audio om identiteit te faken. Groeiend risico door toegankelijke AI-tools.', voorbeeld: 'Deepfake video-call met CFO leidt tot €23 miljoen fraude bij HK-bedrijf.', tip: 'Verificatieprotocollen bij grote transacties, out-of-band bevestiging.' },
  { name: 'DNS Spoofing', level: 'Laag', color: 'bg-indigo-400', cat: 'Netwerk', trend: 'stable', desc: 'Manipulatie van DNS-records om verkeer om te leiden naar kwaadaardige servers.', voorbeeld: 'Cache poisoning bij ISP\'s, DNS hijacking van e-mailverkeer.', tip: 'DNSSEC, DNS monitoring, betrouwbare DNS resolvers.' },
  { name: 'Cryptojacking', level: 'Laag', color: 'bg-emerald-500', cat: 'Malware', trend: 'down', desc: 'Ongeautoriseerd gebruik van rekenkracht voor cryptocurrency mining. Lage impact maar moeilijk detecteerbaar.', voorbeeld: 'Browser-gebaseerde miners, geinfecteerde containers in cloud-omgevingen.', tip: 'Endpoint monitoring, container security, browser-extensies blokkeren.' },
];

const BubbleChart = ({ onBubbleClick }) => {
  const [hoveredThreat, setHoveredThreat] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const scale = (v) => 5 + (v / 100) * 90;

  return (
    <div>
      <div style={{ position: 'relative', width: '100%', height: 460 }}>
        {/* X-axis */}
        <div style={{ position: 'absolute', bottom: 0, left: 44, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: '#a3a3a3' }}>Laag (weinig motivatie)</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#525252', letterSpacing: 1 }}>INTENT →</span>
          <span style={{ fontSize: 9, color: '#ef4444' }}>Hoog (sterke motivatie)</span>
        </div>
        {/* Y-axis */}
        <div style={{ position: 'absolute', top: 24, bottom: 36, left: 0, width: 40, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: '#ef4444', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Hoog (geavanceerd)</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#525252', writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: 1 }}>CAPABILITY →</span>
          <span style={{ fontSize: 9, color: '#a3a3a3', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Laag (beperkt)</span>
        </div>
        {/* Chart area */}
        <div style={{ position: 'absolute', top: 24, left: 44, right: 16, bottom: 36, border: '1px solid #e5e5e5', borderRadius: 6, background: '#fafafa' }}>
          {/* Danger zone */}
          <div style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '45%', background: 'linear-gradient(135deg, rgba(254,242,242,0.8), rgba(254,226,226,0.3))', borderRadius: '0 6px 0 0' }}>
            <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 9, fontWeight: 600, color: '#fca5a5', letterSpacing: 0.5 }}>GEVAARZONE</span>
          </div>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(v => {
            const pos = scale(v);
            return (
              <div key={v}>
                {v > 0 && v < 100 && <div style={{ position: 'absolute', left: `${pos}%`, top: 0, bottom: 0, width: 1, backgroundColor: '#f0f0f0' }} />}
                {v > 0 && v < 100 && <div style={{ position: 'absolute', bottom: `${pos}%`, left: 0, right: 0, height: 1, backgroundColor: '#f0f0f0' }} />}
                <span style={{ position: 'absolute', bottom: -16, left: `${pos}%`, transform: 'translateX(-50%)', fontSize: 10, color: '#a3a3a3', fontWeight: 500 }}>{v}</span>
                <span style={{ position: 'absolute', left: -24, bottom: `${pos}%`, transform: 'translateY(50%)', fontSize: 10, color: '#a3a3a3', textAlign: 'right', width: 20, fontWeight: 500 }}>{v}</span>
              </div>
            );
          })}
          {/* Bubbles */}
          {BUBBLE_THREATS.map((t, i) => {
            const bubbleSize = 28 + (t.impact / 100) * 28;
            const x = scale(t.intent);
            const y = scale(t.capability);
            const isHovered = hoveredThreat === t.name;
            const isDimmed = hoveredThreat && hoveredThreat !== t.name;
            return (
              <div
                key={t.name}
                style={{
                  position: 'absolute',
                  left: `${x}%`,
                  bottom: `${y}%`,
                  transform: `translate(-50%, 50%) scale(${isHovered ? 1.15 : 1})`,
                  zIndex: isHovered ? 50 : 10 + i,
                  transition: 'transform 0.2s ease, opacity 0.2s ease',
                  opacity: isDimmed ? 0.3 : 1,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  setHoveredThreat(t.name);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top });
                }}
                onMouseLeave={() => setHoveredThreat(null)}
                onClick={() => onBubbleClick && onBubbleClick(t.cat)}
                data-testid={`bubble-${t.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div style={{ width: bubbleSize + 14, height: bubbleSize + 14, borderRadius: '50%', backgroundColor: t.fill, opacity: 0.1, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                <div style={{ width: bubbleSize, height: bubbleSize, borderRadius: '50%', backgroundColor: t.fill, opacity: isHovered ? 0.5 : 0.3, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', border: `2px solid ${t.fill}${isHovered ? '80' : '40'}` }} />
                <div style={{ width: bubbleSize * 0.5, height: bubbleSize * 0.5, borderRadius: '50%', backgroundColor: t.fill, opacity: 0.9, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                <span style={{
                  position: 'absolute', bottom: bubbleSize / 2 + 10, left: '50%', transform: 'translateX(-50%)',
                  fontSize: 11, fontWeight: 600, color: '#171717', whiteSpace: 'nowrap',
                  textShadow: '0 0 4px white, 0 0 4px white, 0 0 6px white',
                }}>{t.name}</span>
              </div>
            );
          })}

          {/* Tooltip */}
          {hoveredThreat && (() => {
            const t = BUBBLE_THREATS.find(th => th.name === hoveredThreat);
            if (!t) return null;
            const x = scale(t.intent);
            const y = scale(t.capability);
            return (
              <div
                style={{
                  position: 'absolute',
                  left: `${x}%`,
                  bottom: `${y + 8}%`,
                  transform: 'translate(-50%, 100%)',
                  zIndex: 100,
                  pointerEvents: 'none',
                }}
                data-testid="bubble-tooltip"
              >
                <div className="bg-white rounded-lg shadow-lg border border-neutral-200 px-3 py-2.5 min-w-[160px]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.fill }} />
                    <span className="text-sm font-bold text-black">{t.name}</span>
                  </div>
                  <div className="space-y-2 text-[11px]">
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Intent</span>
                      <span className="font-semibold text-neutral-800">{t.intent}/100</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Capability</span>
                      <span className="font-semibold text-neutral-800">{t.capability}/100</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500">Impact</span>
                      <span className="font-semibold text-neutral-800">{t.impact}/100</span>
                    </div>
                    <div className="pt-1 border-t border-neutral-100 mt-1">
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Risico</span>
                        <span className={`font-bold ${t.intent * t.capability / 100 > 60 ? 'text-red-600' : t.intent * t.capability / 100 > 35 ? 'text-orange-600' : 'text-yellow-600'}`}>
                          {t.intent * t.capability / 100 > 60 ? 'Hoog' : t.intent * t.capability / 100 > 35 ? 'Medium' : 'Laag'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Category color legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 mt-3" data-testid="bubble-chart-legend">
        {[...new Set(BUBBLE_THREATS.map(t => t.cat))].map(cat => {
          const t = BUBBLE_THREATS.find(b => b.cat === cat);
          return (
            <div key={cat} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-neutral-200 text-[11px] font-medium text-neutral-600">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.fill }} />
              {cat}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CategoryActorsPanel = ({ selectedCategory, onCategoryChange, onNavigateToThreats }) => {
  const selectedCat = selectedCategory;
  const setSelectedCat = onCategoryChange;
  const [actorPopup, setActorPopup] = useState(null);
  const categories = [...new Set(BUBBLE_THREATS.map(t => t.cat))];
  const actors = selectedCat ? (CATEGORY_ACTORS[selectedCat] || []) : [];

  const severityColor = {
    'Kritiek': 'bg-red-100 text-red-700 border-red-200',
    'Hoog': 'bg-orange-100 text-orange-700 border-orange-200',
    'Medium': 'bg-amber-100 text-amber-700 border-amber-200',
    'Laag': 'bg-green-100 text-green-700 border-green-200',
  };

  return (
    <Card className="border-neutral-200 shadow-sm h-full" data-testid="actors-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Categorieën & Top Actoren</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Category buttons */}
        <div className="flex flex-wrap gap-1.5">
          {categories.map(cat => {
            const count = BUBBLE_THREATS.filter(t => t.cat === cat).length;
            const isActive = selectedCat === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCat(isActive ? null : cat)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${
                  isActive ? 'bg-black text-white border-black' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                }`}
                data-testid={`cat-btn-${cat.toLowerCase().replace(/\s/g, '-')}`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Actor list */}
        {selectedCat ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-black">Top 5 actoren: {selectedCat}</p>
            {actors.map((actor, i) => (
              <div
                key={i}
                className="p-3 rounded-lg border border-neutral-150 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => setActorPopup(actor)}
                data-testid={`actor-${i}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-black">{i + 1}. {actor.name}</span>
                  <Badge className={`text-[9px] border ${severityColor[actor.severity]}`}>{actor.severity}</Badge>
                </div>
                <p className="text-[10px] text-neutral-500 line-clamp-1">{actor.impact}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Crosshair className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs text-neutral-400">Selecteer een categorie om de top 5 actoren te bekijken</p>
          </div>
        )}
      </CardContent>

      {/* Actor detail popup */}
      {actorPopup && (
        <Dialog open={!!actorPopup} onOpenChange={() => setActorPopup(null)}>
          <DialogContent className="sm:max-w-md" data-testid="actor-detail-popup">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold">{actorPopup.name}</DialogTitle>
              <DialogDescription className="text-xs text-neutral-500">{selectedCat}</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-3">
              <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-2">Impact op categorie</p>
                <p className="text-sm text-red-800 leading-relaxed">{actorPopup.impact}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">Aanbevolen actie</p>
                <p className="text-sm text-emerald-800 leading-relaxed">{actorPopup.action}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Ernst:</span>
                <Badge className={`border ${severityColor[actorPopup.severity]}`}>{actorPopup.severity}</Badge>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">Meenemen naar</p>
                <p className="text-xs text-blue-800 mb-2">Neem deze dreiging mee naar het tabblad "Relevante bedreigingen" om een gerichte mitigatiestrategie op te stellen.</p>
                <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 text-xs" onClick={() => { onNavigateToThreats(actorPopup.name); setActorPopup(null); }} data-testid="navigate-to-relevant-threats">
                  <ArrowRight className="h-3 w-3 mr-1" strokeWidth={1.5} />
                  Bekijk relevante dreigingen
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setActorPopup(null)}>Sluiten</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

const ThreatAnalysisList = () => {
  const [filterLevel, setFilterLevel] = useState('all');
  const [selectedThreat, setSelectedThreat] = useState(null);

  const filtered = filterLevel === 'all' 
    ? THREAT_ANALYSIS 
    : THREAT_ANALYSIS.filter(t => t.level === filterLevel);

  const trendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3 text-red-500" />;
    if (trend === 'down') return <TrendingUp className="h-3 w-3 text-emerald-500 rotate-180" />;
    return <ArrowRight className="h-3 w-3 text-neutral-400" />;
  };

  const trendLabel = (trend) => {
    if (trend === 'up') return 'Stijgend';
    if (trend === 'down') return 'Dalend';
    return 'Stabiel';
  };

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-2 pb-4 mb-2 border-b border-neutral-100" data-testid="analysis-filter-bar">
        {['all', 'Kritiek', 'Hoog', 'Medium', 'Laag'].map(level => (
          <button
            key={level}
            onClick={() => setFilterLevel(level)}
            className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-all ${
              filterLevel === level
                ? 'bg-black text-white border-black'
                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
            }`}
            data-testid={`filter-level-${level.toLowerCase()}`}
          >
            {level === 'all' ? 'Alle' : level}
            {level !== 'all' && (
              <span className="ml-1 opacity-70">
                ({THREAT_ANALYSIS.filter(t => t.level === level).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Threat list - no expand, click opens popup */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((t) => {
          const levelColors = {
            'Kritiek': { dot: 'bg-red-600' },
            'Hoog': { dot: 'bg-orange-500' },
            'Medium': { dot: 'bg-yellow-500' },
            'Laag': { dot: 'bg-emerald-500' },
          };
          const colors = levelColors[t.level] || levelColors['Medium'];

          return (
            <div
              key={t.name}
              className="rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all cursor-pointer"
              onClick={() => setSelectedThreat(t)}
              data-testid={`analysis-${t.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                    <span className="text-sm font-semibold text-black">{t.name}</span>
                  </div>
                  <Badge className="bg-neutral-100 text-neutral-600 text-[10px]">{t.level}</Badge>
                </div>
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="text-[10px] text-neutral-400">{t.cat}</span>
                  <span className="text-[10px] text-neutral-300">|</span>
                  <span className="flex items-center gap-1 text-[10px] text-neutral-500">
                    {trendIcon(t.trend)} {trendLabel(t.trend)}
                  </span>
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed line-clamp-2">{t.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* RightPanel for threat details */}
      <RightPanel
        open={!!selectedThreat}
        onClose={() => setSelectedThreat(null)}
        title={selectedThreat?.name || 'Dreiging'}
        subtitle={selectedThreat ? `${selectedThreat.cat} · ${selectedThreat.level}` : ''}
        testId="threat-analysis-panel"
      >
        {selectedThreat && (
          <div className="space-y-6" data-testid="threat-analysis-popup">
            <div className="flex items-center gap-3">
              <Badge className="bg-neutral-100 text-neutral-600 text-xs px-3 py-1">{selectedThreat.level}</Badge>
              <span className="flex items-center gap-1.5 text-xs text-neutral-500">
                {trendIcon(selectedThreat.trend)} {trendLabel(selectedThreat.trend)}
              </span>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Beschrijving</p>
              <p className="text-sm text-neutral-700 leading-relaxed">{selectedThreat.desc}</p>
            </div>
            <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Voorbeeld</p>
              <p className="text-sm text-neutral-600 italic leading-relaxed">{selectedThreat.voorbeeld}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">Mitigatie</p>
              <p className="text-sm text-emerald-800 leading-relaxed">{selectedThreat.tip}</p>
            </div>
          </div>
        )}
      </RightPanel>
    </div>
  );
};

// Radar chart data: 5 sectors with cybersecurity threats
const RADAR_CATEGORIES = [
  { key: 'samenleving', label: 'Samenleving', icon: Users, centerAngle: 162 },
  { key: 'technologie', label: 'Technologie &\nInnovatie', icon: Zap, centerAngle: 126 },
  { key: 'economie', label: 'Economie &\nBusiness', icon: TrendingUp, centerAngle: 90 },
  { key: 'omgeving', label: 'Omgeving', icon: Globe, centerAngle: 54 },
  { key: 'wetgeving', label: 'Wet &\nBeleid', icon: Shield, centerAngle: 18 },
];

const RADAR_THREATS = [
  // Economie & Business (sector 72°-108°)
  { id: 'r1', name: 'Ransomware', category: 'economie', angle: 97, distance: 82, impact: 'high',
    desc: 'Versleutelt bestanden en eist losgeld. Steeds vaker gecombineerd met data-exfiltratie (dubbele afpersing). Grootste financiele dreiging voor het MKB.',
    voorbeeld: 'LockBit, BlackCat, Cl0p: gemiddelde schade €250.000+. In 2025 steeg ransomware met 40%.',
    tip: 'Offline backups, netwerksegmentatie, EDR, incident response plan, cyber verzekering.' },
  { id: 'r2', name: 'Supply Chain', category: 'economie', angle: 83, distance: 58, impact: 'high',
    desc: 'Aanvallers infiltreren via vertrouwde leveranciers. Moeilijk te detecteren door legitieme bronnen.',
    voorbeeld: 'SolarWinds Orion, Kaseya VSA, 3CX: malware via software-updates van leveranciers.',
    tip: 'Leveranciers-assessments, code signing verificatie, SBOM, minimale rechten.' },
  { id: 'r3', name: 'BEC Fraude', category: 'economie', angle: 92, distance: 40, impact: 'medium',
    desc: 'Business Email Compromise: gerichte e-mailfraude waarbij aanvallers zich voordoen als directie of leveranciers.',
    voorbeeld: 'CEO-fraude: nep-facturen van €50.000+ via gehackte e-mailaccounts.',
    tip: 'Verificatieprotocollen voor betalingen, DMARC/SPF/DKIM, awareness training.' },
  { id: 'r4', name: 'Cryptojacking', category: 'economie', angle: 78, distance: 25, impact: 'low',
    desc: 'Ongeautoriseerd gebruik van rekenkracht voor cryptocurrency mining. Lage directe impact maar wijst op compromitatie.',
    voorbeeld: 'Browser-gebaseerde miners, geinfecteerde containers in cloud-omgevingen.',
    tip: 'Endpoint monitoring, container security, browser-extensies blokkeren.' },

  // Technologie & Innovatie (sector 108°-144°)
  { id: 'r5', name: 'AI-aanvallen', category: 'technologie', angle: 135, distance: 68, impact: 'high',
    desc: 'AI wordt ingezet om aanvallen te automatiseren, phishing te personaliseren en beveiligingen te omzeilen.',
    voorbeeld: 'AI-gegenereerde spear-phishing, geautomatiseerde kwetsbaarheid-scanning, deepfake vishing.',
    tip: 'AI-gebaseerde detectie, gedragsanalyse, continue monitoring, red team oefeningen.' },
  { id: 'r6', name: 'Zero-day', category: 'technologie', angle: 115, distance: 52, impact: 'high',
    desc: 'Onbekende kwetsbaarheden zonder beschikbare patch. Worden verhandeld op de zwarte markt.',
    voorbeeld: 'Log4Shell, MOVEit Transfer: massale exploitatie voordat patches beschikbaar waren.',
    tip: 'WAF, virtual patching, threat intelligence feeds, minimale aanvalsoppervlakte.' },
  { id: 'r7', name: 'IoT exploits', category: 'technologie', angle: 125, distance: 42, impact: 'medium',
    desc: 'Onbeveiligde IoT-apparaten als springplank naar het netwerk. Standaard wachtwoorden zijn een groot risico.',
    voorbeeld: 'Mirai botnet, camera-hacks, slimme gebouwbeheer-systemen als toegangspoort.',
    tip: 'Netwerksegmentatie voor IoT, firmware updates, standaard credentials wijzigen.' },
  { id: 'r8', name: 'Quantum risico', category: 'technologie', angle: 140, distance: 26, impact: 'low',
    desc: 'Toekomstige quantum computers kunnen huidige encryptie breken. "Harvest now, decrypt later" strategie.',
    voorbeeld: 'Staatshackers verzamelen nu versleutelde data om later met quantum te ontcijferen.',
    tip: 'Post-quantum cryptografie plannen, inventarisatie van cryptografische assets.' },

  // Samenleving (sector 144°-180°)
  { id: 'r9', name: 'Phishing', category: 'samenleving', angle: 168, distance: 75, impact: 'high',
    desc: '91% van cyberaanvallen start met phishing. Door AI worden e-mails steeds overtuigender en moeilijker te herkennen.',
    voorbeeld: 'Valse facturen, CEO-fraude e-mails, spear-phishing op financiele afdelingen.',
    tip: 'Security awareness training, DMARC/SPF/DKIM, MFA op alle accounts.' },
  { id: 'r10', name: 'Social engineering', category: 'samenleving', angle: 152, distance: 55, impact: 'high',
    desc: 'Manipulatie van medewerkers om toegang of informatie te verkrijgen. Combineert psychologie met technologie.',
    voorbeeld: 'Vishing (telefonisch), pretexting, baiting met USB-sticks op parkeerplaatsen.',
    tip: 'Bewustzijnstraining, verificatieprotocollen, meldcultuur stimuleren.' },
  { id: 'r11', name: 'Identiteitsfraude', category: 'samenleving', angle: 162, distance: 38, impact: 'medium',
    desc: 'Misbruik van gestolen persoonsgegevens voor frauduleuze doeleinden. Groeit door datalekken.',
    voorbeeld: 'Credential stuffing: miljarden gelekte wachtwoorden worden hergebruikt op bedrijfssystemen.',
    tip: 'MFA verplicht, wachtwoordbeleid, credential monitoring (Have I Been Pwned).' },
  { id: 'r12', name: 'Deepfake', category: 'samenleving', angle: 175, distance: 24, impact: 'low',
    desc: 'AI-gegenereerde video/audio om identiteit te faken. Groeiend risico door toegankelijke AI-tools.',
    voorbeeld: 'Deepfake video-call met CFO leidt tot €23 miljoen fraude bij HK-bedrijf.',
    tip: 'Verificatieprotocollen bij grote transacties, out-of-band bevestiging.' },

  // Omgeving / Extern (sector 36°-72°)
  { id: 'r13', name: 'Staatshackers', category: 'omgeving', angle: 62, distance: 72, impact: 'high',
    desc: 'Advanced Persistent Threats (APT) gesponsord door natiestaten. Zeer geavanceerd en persistent.',
    voorbeeld: 'APT29 (Rusland), APT41 (China): gerichte aanvallen op kritieke infrastructuur.',
    tip: 'Threat intelligence, geavanceerde detectie, netwerksegmentatie, incident response readiness.' },
  { id: 'r14', name: 'DDoS', category: 'omgeving', angle: 48, distance: 50, impact: 'high',
    desc: 'Overbelasting van systemen. Vaak als afleiding voor gelijktijdige data-exfiltratie.',
    voorbeeld: 'Aanvallen op banken en overheid, volumetrisch tot 1Tbps.',
    tip: 'CDN, rate limiting, anti-DDoS diensten, redundante infrastructuur.' },
  { id: 'r15', name: 'Hacktivisme', category: 'omgeving', angle: 55, distance: 38, impact: 'medium',
    desc: 'Politiek gemotiveerde cyberaanvallen. Doelwitten gebaseerd op publieke standpunten.',
    voorbeeld: 'Website defacement, DDoS op overheidsinstanties, datalekken als protest.',
    tip: 'Monitoring van dreigingslandschap, DDoS-mitigatie, security awareness.' },
  { id: 'r16', name: 'Dark web', category: 'omgeving', angle: 42, distance: 28, impact: 'medium',
    desc: 'Handel in gestolen data, malware-kits en toegang tot gecompromitteerde systemen.',
    voorbeeld: 'Ransomware-as-a-Service, verkoop van bedrijfstoegangen voor €500-€50.000.',
    tip: 'Dark web monitoring, credential leak detectie, threat intelligence abonnementen.' },

  // Wet & Beleid (sector 0°-36°)
  { id: 'r17', name: 'NIS2 richtlijn', category: 'wetgeving', angle: 26, distance: 65, impact: 'high',
    desc: 'EU-richtlijn voor cybersecurity die strengere eisen stelt aan essentiële en belangrijke entiteiten.',
    voorbeeld: 'Boetes tot €10 miljoen of 2% van wereldwijde omzet bij non-compliance.',
    tip: 'Gap-analyse uitvoeren, incident response plan opstellen, supply chain security borgen.' },
  { id: 'r18', name: 'AVG/GDPR', category: 'wetgeving', angle: 12, distance: 48, impact: 'medium',
    desc: 'Privacy-regulering met strenge eisen voor verwerking van persoonsgegevens.',
    voorbeeld: 'AP boetes in NL: €830.000 voor verkeerd gebruik gezichtsherkenning.',
    tip: 'Privacy Impact Assessments, verwerkingsregister, DPO aanstellen, data minimalisatie.' },
  { id: 'r19', name: 'Meldplicht', category: 'wetgeving', angle: 20, distance: 36, impact: 'medium',
    desc: 'Wettelijke plicht om datalekken te melden bij de AP en betrokkenen. Strikte termijnen (72 uur).',
    voorbeeld: 'Boetes voor te laat melden van datalekken, reputatieschade bij publieke melding.',
    tip: 'Datalekprocedure opstellen en testen, registratie bijhouden, medewerkers trainen.' },
  { id: 'r20', name: 'Sector eisen', category: 'wetgeving', angle: 6, distance: 22, impact: 'low',
    desc: 'Sector-specifieke cybersecurity eisen (financieel: DORA, zorg: NEN 7510, overheid: BIO).',
    voorbeeld: 'DORA: financiële sector moet ICT-risicobeheer aantonen, penetratietesten uitvoeren.',
    tip: 'Relevante frameworks identificeren, compliance roadmap opstellen, certificering plannen.' },
];

const ThreatRadarChart = ({ selected, onSelect }) => {
  const [hovered, setHovered] = useState(null);
  const CX = 500, CY = 520, MAX_R = 400;
  const toRad = (d) => d * Math.PI / 180;
  const getPos = (angle, dist) => ({
    x: CX + (dist / 100) * MAX_R * Math.cos(toRad(angle)),
    y: CY - (dist / 100) * MAX_R * Math.sin(toRad(angle))
  });

  const matR = 0.2 * MAX_R;
  const impactStyle = {
    high: { fill: '#1e3a5f', r: 9 },
    medium: { fill: '#84cc16', r: 7 },
    low: { fill: '#eab308', r: 5 },
  };

  // Build SVG inner HTML as string to avoid Emergent editor attribute injection on mapped elements
  const svgContent = useMemo(() => {
    let s = '';
    // Zone circles (largest to smallest)
    s += `<circle cx="${CX}" cy="${CY}" r="${MAX_R}" fill="#e8e8e8" />`;
    s += `<circle cx="${CX}" cy="${CY}" r="${0.8*MAX_R}" fill="#d6d6d6" />`;
    s += `<circle cx="${CX}" cy="${CY}" r="${0.6*MAX_R}" fill="#c2c2c2" />`;
    s += `<circle cx="${CX}" cy="${CY}" r="${0.4*MAX_R}" fill="#adadad" />`;
    s += `<circle cx="${CX}" cy="${CY}" r="${matR}" fill="#eab308" />`;
    // Cover bottom half
    s += `<rect x="0" y="${CY}" width="1000" height="80" fill="white" />`;
    // MATURITEIT text
    s += `<text x="${CX}" y="${CY - matR * 0.3}" text-anchor="middle" font-size="13" font-weight="700" fill="white" letter-spacing="2">MATURITEIT</text>`;
    // Sector dividing lines
    [0, 36, 72, 108, 144, 180].forEach(a => {
      const end = getPos(a, 100);
      s += `<line x1="${CX}" y1="${CY}" x2="${end.x}" y2="${end.y}" stroke="white" stroke-width="2.5" />`;
    });
    // X-axis
    s += `<line x1="${CX - MAX_R - 25}" y1="${CY}" x2="${CX + MAX_R + 25}" y2="${CY}" stroke="#404040" stroke-width="1.5" />`;
    s += `<polygon points="${CX - MAX_R - 25},${CY} ${CX - MAX_R - 15},${CY - 5} ${CX - MAX_R - 15},${CY + 5}" fill="#404040" />`;
    s += `<polygon points="${CX + MAX_R + 25},${CY} ${CX + MAX_R + 15},${CY - 5} ${CX + MAX_R + 15},${CY + 5}" fill="#404040" />`;
    // Axis ticks + numbers
    [20, 40, 60, 80, 100].forEach(d => {
      const r = (d / 100) * MAX_R;
      s += `<line x1="${CX+r}" y1="${CY-3}" x2="${CX+r}" y2="${CY+3}" stroke="#666" stroke-width="1" />`;
      s += `<line x1="${CX-r}" y1="${CY-3}" x2="${CX-r}" y2="${CY+3}" stroke="#666" stroke-width="1" />`;
      s += `<text x="${CX+r}" y="${CY+18}" text-anchor="middle" font-size="11" fill="#737373" font-weight="500">${d}</text>`;
      s += `<text x="${CX-r}" y="${CY+18}" text-anchor="middle" font-size="11" fill="#737373" font-weight="500">${d}</text>`;
    });
    // Threat dots
    RADAR_THREATS.forEach(t => {
      const p = getPos(t.angle, t.distance);
      const st = impactStyle[t.impact];
      const isActive = selected === t.id || hovered === t.id;
      const isDimmed = (selected || hovered) && selected !== t.id && hovered !== t.id;
      const dotR = isActive ? st.r + 2 : st.r;
      const opacity = isDimmed ? 0.25 : 1;
      const textFill = isDimmed ? '#ccc' : '#333';
      s += `<g data-id="${t.id}" style="cursor:pointer">`;
      if (selected === t.id) {
        s += `<circle cx="${p.x}" cy="${p.y}" r="${st.r + 7}" fill="none" stroke="${st.fill}" stroke-width="2" stroke-dasharray="4 2" opacity="0.7" />`;
      }
      s += `<circle cx="${p.x}" cy="${p.y}" r="${dotR}" fill="${st.fill}" opacity="${opacity}" />`;
      s += `<text x="${p.x}" y="${p.y - st.r - 5}" text-anchor="middle" font-size="9.5" font-weight="600" fill="${textFill}" style="pointer-events:none">${t.name}</text>`;
      s += `</g>`;
    });
    return s;
  }, [selected, hovered]);

  const handleSvgClick = (e) => {
    const g = e.target.closest('[data-id]');
    if (g) {
      const id = g.dataset.id;
      onSelect(selected === id ? null : id);
    }
  };

  const handleSvgMouseOver = (e) => {
    const g = e.target.closest('[data-id]');
    setHovered(g ? g.dataset.id : null);
  };

  // Compute label positions for HTML overlay
  const catPositions = RADAR_CATEGORIES.map(cat => {
    const lp = getPos(cat.centerAngle, 113);
    return { ...cat, px: (lp.x / 1000) * 100, py: (lp.y / 600) * 100 };
  });

  return (
    <div className="relative" data-testid="threat-radar-container">
      <svg
        viewBox="0 0 1000 600"
        className="w-full"
        data-testid="threat-radar-svg"
        dangerouslySetInnerHTML={{ __html: svgContent }}
        onClick={handleSvgClick}
        onMouseOver={handleSvgMouseOver}
        onMouseLeave={() => setHovered(null)}
      />
      {/* Category labels as HTML overlays */}
      {catPositions.map(cat => {
        const Icon = cat.icon;
        return (
          <div
            key={cat.key}
            className="absolute flex flex-col items-center gap-1 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ left: `${cat.px}%`, top: `${cat.py}%` }}
            data-testid={`radar-cat-${cat.key}`}
          >
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e3a5f' }}>
              <Icon className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-[11px] font-bold text-center leading-tight whitespace-pre-line" style={{ color: '#1e3a5f' }}>
              {cat.label.replace('\n', '\n')}
            </span>
          </div>
        );
      })}
      {/* Impact legend overlay */}
      <div className="absolute top-2 left-2 bg-white border border-neutral-200 rounded-lg p-2.5 shadow-sm pointer-events-none">
        <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Impact</div>
        {[
          { color: '#eab308', size: 10, label: 'Laag' },
          { color: '#84cc16', size: 14, label: 'Midden' },
          { color: '#1e3a5f', size: 18, label: 'Hoog' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-2 py-0.5">
            <div className="rounded-full shrink-0" style={{ width: l.size, height: l.size, backgroundColor: l.color }} />
            <span className="text-[11px] text-neutral-600">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

import { PageShell, PageToolbar, SectionTabs, SECTIONS } from '../components/enterprise';

const ThreatsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [news, setNews] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterOrgSeverity, setFilterOrgSeverity] = useState('all');
  const [filterOrgHasAssets, setFilterOrgHasAssets] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [activeTab, setActiveTab] = useState('relevant');
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loadingVulns, setLoadingVulns] = useState(true);
  const [selectedRadarThreat, setSelectedRadarThreat] = useState(null);
  const [selectedBubbleCategory, setSelectedBubbleCategory] = useState(null);
  const [orgAssets, setOrgAssets] = useState([]);
  const [orgThreats, setOrgThreats] = useState([]);
  const [detailOrgThreat, setDetailOrgThreat] = useState(null);

  useEffect(() => {
    fetchNews();
    fetchVulnerabilities();
    fetchOrgAssets();
    fetchOrgThreats();
  }, []);

  const fetchOrgAssets = async () => {
    try {
      const res = await api.get('/ggrc/customer-assets');
      setOrgAssets(res.data.assets || []);
    } catch { /* silent */ }
  };

  const fetchOrgThreats = async () => {
    try {
      const res = await api.get('/ggrc/threats');
      setOrgThreats(res.data.threats || []);
    } catch { /* silent */ }
  };

  const fetchNews = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await api.get('/security-news');
      setNews(response.data.items || []);
      setLastUpdated(response.data.last_updated);
      if (isRefresh) toast.success('Aanvallen bijgewerkt');
    } catch (error) {
      toast.error('Aanvallen laden mislukt');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchVulnerabilities = async () => {
    try {
      const response = await api.get('/ggrc/vulnerabilities');
      setVulnerabilities(response.data?.vulnerabilities || []);
    } catch {
      // Silently fail
    } finally {
      setLoadingVulns(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-NL', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Count by category
  const categoryCounts = news.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {});

  // Filter and sort news
  const filteredNews = news.filter(item => {
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesSeverity = filterSeverity === 'all' || item.severity === filterSeverity;
    return matchesSearch && matchesCategory && matchesSeverity;
  }).sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
    } else if (sortBy === 'severity') {
      const severityOrder = { critical: 0, high: 1, medium: 2, info: 3 };
      return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
    }
    return 0;
  });

  if (loading) {
    return <PageShell section="risicos" testId="threats-page" loading />;
  }

  const highThreats = orgThreats.filter(t => t.severity === 'critical' || t.severity === 'high').length;

  return (
    <PageShell section="risicos" testId="threats-page">
      <PageToolbar
        title="Dreigingen"
        subtitle={orgThreats.length === 0 ? 'Controleer waar je organisatie kwetsbaar is'
          : highThreats > 0 ? `${highThreats} ernstige ${highThreats === 1 ? 'dreiging' : 'dreigingen'} vereisen aandacht`
          : 'Je bent voorbereid op de meeste dreigingen'}
        count={orgThreats.length > 0 ? orgThreats.length : undefined}
        testId="threats"
      />

      <div className="px-6 py-6">
          <ContextualTip pageKey="threats" tip="Het tabblad 'Relevant voor jou' toont alleen aanvallen die bij jouw situatie passen op basis van je nulmeting." />

          <div className="space-y-8 mt-6">
            {/* STATUS */}
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="threats-status-section">
              <p className="spk-section-label">Status</p>
              {orgThreats.length === 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">Er zijn nog geen aanvallen gekoppeld aan je organisatie</h2>
                  <p className="mt-3 text-neutral-600">
                    Bekijk het dreigingslandschap om te begrijpen welke aanvallen relevant zijn voor jouw situatie.
                  </p>
                </>
              ) : highThreats > 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">
                    {highThreats} {highThreats === 1 ? 'aanval is' : 'aanvallen zijn'} relevant en ernstig voor je organisatie
                  </h2>
                  <p className="mt-3 text-neutral-600">
                    Zorg dat je oplossingen hebt getroffen tegen deze aanvallen.
                  </p>
                  <Button
                    onClick={() => navigate('/risks')}
                    className="mt-4 bg-black text-white hover:bg-neutral-800"
                    data-testid="threats-goto-risks-cta"
                  >
                    Bekijk gerelateerde risico's
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mt-2">
                    Je hebt {orgThreats.length} mogelijke {orgThreats.length === 1 ? 'aanval' : 'aanvallen'} in kaart gebracht
                  </h2>
                  <p className="mt-3 text-neutral-600">
                    Geen ernstige bedreigingen gevonden. Blijf het landschap in de gaten houden.
                  </p>
                </>
              )}
            </section>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex justify-start bg-neutral-100 p-1 rounded-lg h-auto">
            <TabsTrigger 
              value="relevant" 
              className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5"
              data-testid="tab-relevant"
            >
              <Target className="h-3.5 w-3.5" strokeWidth={1.5} />
              Relevant voor jou
            </TabsTrigger>
            <TabsTrigger 
              value="landschap" 
              className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5"
              data-testid="tab-landschap"
            >
              <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
              Alle aanvallen
            </TabsTrigger>
            <TabsTrigger 
              value="actueel" 
              className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5"
              data-testid="tab-actueel"
            >
              <Crosshair className="h-3.5 w-3.5" strokeWidth={1.5} />
              Actueel nieuws
            </TabsTrigger>
          </TabsList>

          {/* TAB 0: Dreigingslandschap - Split View */}
          <TabsContent value="landschap" className="mt-6 space-y-6">
            {/* Split: Bubble Chart + Category Actors Panel */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Bubble Chart */}
              <div className="xl:col-span-3">
                <Card className="border-neutral-200 shadow-sm" data-testid="threat-landscape-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-black">Aanvalslandschap</CardTitle>
                    <CardDescription className="text-xs text-neutral-500">Klik op een categorie rechts om de bekendste aanvallers te bekijken</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BubbleChart onBubbleClick={(cat) => setSelectedBubbleCategory(cat)} />
                  </CardContent>
                </Card>
              </div>

              {/* Category Actors Panel */}
              <div className="xl:col-span-2" data-testid="category-actors-panel">
                <CategoryActorsPanel selectedCategory={selectedBubbleCategory} onCategoryChange={setSelectedBubbleCategory} onNavigateToThreats={(actorName) => { setActiveTab('relevant'); setSearchQuery(actorName); }} />
              </div>
            </div>

            {/* Timeline Chart: 2025-2027 */}
            <Card className="border-neutral-200 shadow-sm" data-testid="threat-timeline-chart">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold text-black">Aanvalstrend 2025–2027</CardTitle>
                    <CardDescription className="text-xs text-neutral-500">
                      Incidenten per categorie per kwartaal. Gestippelde lijnen zijn verwachtingen.
                    </CardDescription>
                  </div>
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">
                    <Activity className="h-3 w-3 mr-1" />
                    Deels voorspelling
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <AreaChart data={THREAT_TIMELINE_DATA} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradRansomware" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#b91c1c" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#b91c1c" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradPhishing" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.12} />
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradSupplyChain" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="periode" tick={{ fontSize: 11, fill: '#737373' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#737373' }} />
                      <RechartsTooltip
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e5e5', fontSize: 12 }}
                        labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                        formatter={(value, name) => {
                          const labels = { ransomware: 'Ransomware', phishing: 'Phishing', supply_chain: 'Supply Chain', zero_day: 'Zero-day', ddos: 'DDoS', insider: 'Insider' };
                          return [value, labels[name] || name];
                        }}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        formatter={(value) => {
                          const labels = { ransomware: 'Ransomware', phishing: 'Phishing', supply_chain: 'Supply Chain', zero_day: 'Zero-day', ddos: 'DDoS', insider: 'Insider', complexiteit: 'Complexiteit' };
                          return <span style={{ fontSize: 11, color: '#525252' }}>{labels[value] || value}</span>;
                        }}
                      />
                      <ReferenceLine x="Q2 2026" stroke="#a3a3a3" strokeDasharray="6 4" label={{ value: 'Nu', position: 'top', fontSize: 10, fill: '#737373' }} />
                      <Area type="monotone" dataKey="ransomware" stroke="#b91c1c" fill="url(#gradRansomware)" strokeWidth={2} dot={false} />
                      <Area type="monotone" dataKey="phishing" stroke="#ef4444" fill="url(#gradPhishing)" strokeWidth={2} dot={false} />
                      <Area type="monotone" dataKey="supply_chain" stroke="#f97316" fill="url(#gradSupplyChain)" strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="zero_day" stroke="#8b5cf6" fill="none" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                      <Area type="monotone" dataKey="ddos" stroke="#3b82f6" fill="none" strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="insider" stroke="#eab308" fill="none" strokeWidth={1.5} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-neutral-100">
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-0.5 bg-neutral-400" />
                    <span className="text-[10px] text-neutral-500">Historisch</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-0.5 bg-neutral-400" style={{ borderTop: '2px dashed #a3a3a3', height: 0 }} />
                    <span className="text-[10px] text-neutral-500">Voorspelling</span>
                  </div>
                  <span className="text-[10px] text-neutral-400 ml-auto">Bron: Trendanalyse op basis van NCSC & sector-rapportages</span>
                </div>
              </CardContent>
            </Card>

            {/* Analysis per threat - expandable list */}
            <Card className="border-neutral-200 shadow-sm" data-testid="threat-analysis-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold text-black">Analyse per aanval</CardTitle>
                    <CardDescription className="text-xs text-neutral-500">
                      Gedetailleerde analyse van {THREAT_ANALYSIS.length} aanvallen met voorbeelden en aanbevelingen
                    </CardDescription>
                  </div>
                  <Badge className="bg-neutral-100 text-neutral-600 text-[10px]">{THREAT_ANALYSIS.length} aanvallen</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ThreatAnalysisList />
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Actueel nieuws — Radar Chart */}
          <TabsContent value="actueel" className="mt-6 space-y-6">
            {/* Overlay: toekomstige uitbreiding */}
            <Card className="border-amber-200 bg-amber-50/50 shadow-sm" data-testid="actueel-overlay">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Binnenkort beschikbaar</p>
                  <p className="text-xs text-amber-700">
                    Dit tabblad wordt uitgebreid met actueel nieuws over aanvallen en automatische koppeling aan jouw systemen en zwakke plekken.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Radar Chart */}
              <div className="xl:col-span-3" data-testid="radar-chart-container">
                <Card className="border-neutral-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-black">Actueel aanvalsradar</CardTitle>
                    <CardDescription className="text-xs text-neutral-500">
                      20 aanvallen verdeeld over 5 categorieen. Klik op een aanval voor details.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-2 pb-2">
                    <ThreatRadarChart selected={selectedRadarThreat} onSelect={setSelectedRadarThreat} />
                  </CardContent>
                </Card>
              </div>

              {/* Detail Panel */}
              <div className="xl:col-span-2" data-testid="radar-detail-panel">
                {selectedRadarThreat ? (() => {
                  const threat = RADAR_THREATS.find(t => t.id === selectedRadarThreat);
                  if (!threat) return null;
                  const cat = RADAR_CATEGORIES.find(c => c.key === threat.category);
                  const CatIcon = cat?.icon || Shield;
                  const impactColors = {
                    high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Hoog' },
                    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Midden' },
                    low: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Laag' },
                  };
                  const ic = impactColors[threat.impact];

                  return (
                    <Card className="border-neutral-200 shadow-sm sticky top-20" data-testid="radar-threat-detail">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e3a5f' }}>
                            <CatIcon className="h-5 w-5 text-white" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg font-bold text-black">{threat.name}</CardTitle>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-neutral-500">{cat?.label?.replace('\n', ' ')}</span>
                              <Badge className={`${ic.bg} ${ic.text} border ${ic.border} text-[10px]`}>{ic.label} impact</Badge>
                            </div>
                          </div>
                          <button onClick={() => setSelectedRadarThreat(null)}
                            className="p-1 hover:bg-neutral-100 rounded-md" data-testid="radar-detail-close">
                            <ChevronUp className="h-4 w-4 text-neutral-400" />
                          </button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Description */}
                        <div>
                          <h4 className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wide mb-2">Beschrijving</h4>
                          <p className="text-sm text-neutral-700 leading-relaxed">{threat.desc}</p>
                        </div>

                        {/* Example */}
                        <div className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                          <h4 className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide mb-2">Voorbeeld</h4>
                          <p className="text-xs text-neutral-600 italic leading-relaxed">{threat.voorbeeld}</p>
                        </div>

                        {/* Mitigation */}
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                          <h4 className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">Mitigatie</h4>
                          <p className="text-xs text-emerald-800 leading-relaxed">{threat.tip}</p>
                        </div>

                        {/* Positioning info */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-neutral-100">
                          <div className="text-center p-2 bg-neutral-50 rounded-lg">
                            <p className="text-lg font-bold text-black">{threat.distance}</p>
                            <p className="text-[10px] text-neutral-500">Bereik / scope</p>
                          </div>
                          <div className="text-center p-2 bg-neutral-50 rounded-lg">
                            <p className="text-lg font-bold text-black">{ic.label}</p>
                            <p className="text-[10px] text-neutral-500">Impact niveau</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })() : (
                  <Card className="border-neutral-200 shadow-sm border-dashed" data-testid="radar-detail-empty">
                    <CardContent className="py-16 text-center">
                      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-neutral-100 mb-4">
                        <Crosshair className="h-7 w-7 text-neutral-400" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-base font-semibold text-black mb-2">Selecteer een dreiging</h3>
                      <p className="text-sm text-neutral-500 max-w-xs mx-auto">
                        Klik op een punt in het radar diagram om de gedetailleerde analyse te bekijken
                      </p>
                      <div className="flex items-center justify-center gap-4 mt-5">
                        {[
                          { color: '#eab308', size: 8, label: 'Laag' },
                          { color: '#84cc16', size: 10, label: 'Midden' },
                          { color: '#1e3a5f', size: 12, label: 'Hoog' },
                        ].map(l => (
                          <div key={l.label} className="flex items-center gap-1.5">
                            <div className="rounded-full" style={{ width: l.size, height: l.size, backgroundColor: l.color }} />
                            <span className="text-[11px] text-neutral-500">{l.label}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Category overview mini cards */}
                <div className="grid grid-cols-1 gap-3 mt-4" data-testid="radar-category-overview">
                  {RADAR_CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    const catThreats = RADAR_THREATS.filter(t => t.category === cat.key);
                    const highCount = catThreats.filter(t => t.impact === 'high').length;
                    return (
                      <div key={cat.key}
                        className="flex items-center gap-3 p-3 rounded-lg border border-neutral-100 bg-white hover:bg-neutral-50 transition-colors"
                        data-testid={`radar-cat-card-${cat.key}`}
                      >
                        <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#1e3a5f' }}>
                          <Icon className="h-4 w-4 text-white" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold text-black">{cat.label.replace('\n', ' ')}</span>
                          <p className="text-[10px] text-neutral-500">{catThreats.length} dreigingen</p>
                        </div>
                        {highCount > 0 && (
                          <Badge className="bg-red-50 text-red-700 text-[10px]">{highCount} hoog</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB: Relevante aanvallen op basis van je zwakke plekken */}
          <TabsContent value="relevant" className="mt-6 space-y-6">
            {/* Explanation */}
            <Card className="border-blue-200 bg-blue-50 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-800">Op basis van jouw situatie</p>
                    <p className="text-xs text-blue-700">
                      Deze aanvallen zijn relevant omdat ze aansluiten bij de zwakke plekken die we bij jou gevonden hebben
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filters voor org-dreigingen */}
            {orgThreats.length > 0 && (
              <div className="flex flex-wrap items-center gap-3" data-testid="threat-org-filters">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <input
                    type="text"
                    placeholder="Zoek in dreigingen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                    data-testid="threat-search-input"
                  />
                </div>
                <Select value={filterOrgSeverity} onValueChange={setFilterOrgSeverity}>
                  <SelectTrigger className="w-36 border-neutral-200" data-testid="threat-severity-filter">
                    <SelectValue placeholder="Ernst" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle niveaus</SelectItem>
                    <SelectItem value="critical">Kritiek</SelectItem>
                    <SelectItem value="high">Hoog</SelectItem>
                    <SelectItem value="medium">Gemiddeld</SelectItem>
                    <SelectItem value="low">Laag</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterOrgHasAssets} onValueChange={setFilterOrgHasAssets}>
                  <SelectTrigger className="w-44 border-neutral-200" data-testid="threat-assets-filter">
                    <SelectValue placeholder="Systemen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle dreigingen</SelectItem>
                    <SelectItem value="with">Met systemen</SelectItem>
                    <SelectItem value="without">Zonder systemen</SelectItem>
                  </SelectContent>
                </Select>
                {(searchQuery || filterOrgSeverity !== 'all' || filterOrgHasAssets !== 'all') && (
                  <Button variant="ghost" size="sm" className="text-xs text-neutral-500 h-8"
                    onClick={() => { setSearchQuery(''); setFilterOrgSeverity('all'); setFilterOrgHasAssets('all'); }}
                    data-testid="threat-clear-filters">
                    <Filter className="h-3 w-3 mr-1" strokeWidth={1.5} />Wis filters
                  </Button>
                )}
              </div>
            )}

            {/* Relatietabel */}
            {orgThreats.length > 0 && (() => {
              const filteredOrgThreats = orgThreats.filter(threat => {
                const matchesSearch = !searchQuery || [threat.title, threat.threat_id, threat.description]
                  .some(f => (f || '').toLowerCase().includes(searchQuery.toLowerCase()));
                const matchesSeverity = filterOrgSeverity === 'all' || threat.severity === filterOrgSeverity;
                const matchesAssets = filterOrgHasAssets === 'all' ||
                  (filterOrgHasAssets === 'with' ? (threat.asset_ids || []).length > 0 : (threat.asset_ids || []).length === 0);
                return matchesSearch && matchesSeverity && matchesAssets;
              });
              return filteredOrgThreats.length > 0 ? (
              <Card className="border-neutral-200 shadow-sm" data-testid="threat-relationship-table">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-black">Hoe aanvallen verbonden zijn met je systemen</CardTitle>
                  <CardDescription className="text-xs text-neutral-500">
                    Per aanval zie je welke systemen en zwakke plekken erbij betrokken zijn
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="threat-chain-table">
                      <thead>
                        <tr className="border-b border-neutral-200">
                          <th className="p-4 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">ID</th>
                          <th className="p-4 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">Aanval</th>
                          <th className="p-4 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">Systemen</th>
                          <th className="p-4 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">Zwakke plekken</th>
                          <th className="p-4 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">Kans</th>
                          <th className="p-4 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-50">BIV Impact</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {filteredOrgThreats.map(threat => {
                          const linkedAssets = orgAssets.filter(a => (threat.asset_ids || []).includes(a.id));
                          const linkedVulns = vulnerabilities.filter(v => (threat.vulnerability_ids || []).includes(v.id));
                          const avgImpact = Math.round(((threat.impact_confidentiality || 3) + (threat.impact_integrity || 3) + (threat.impact_availability || 3)) / 3);
                          return (
                            <tr key={threat.id} className="hover:bg-neutral-50 cursor-pointer transition-colors" data-testid={`threat-rel-row-${threat.threat_id}`}
                              onClick={() => setDetailOrgThreat({ ...threat, linkedAssets, linkedVulns, avgImpact })}
                            >
                              <td className="p-4 text-xs font-mono text-neutral-500">{threat.threat_id}</td>
                              <td className="p-4">
                                <p className="text-sm font-medium text-black">{threat.title}</p>
                                {threat.description && <p className="text-[10px] text-neutral-500 mt-0.5 line-clamp-1">{threat.description}</p>}
                              </td>
                              <td className="p-4">
                                {linkedAssets.length > 0 ? linkedAssets.map(a => (
                                  <Badge key={a.id} variant="outline" className="mr-1 mb-1 text-[10px] border-blue-200 text-blue-700 bg-blue-50">{a.name}</Badge>
                                )) : <span className="text-xs text-neutral-400">-</span>}
                              </td>
                              <td className="p-4">
                                {linkedVulns.length > 0 ? linkedVulns.map(v => (
                                  <Badge key={v.id} variant="outline" className="mr-1 mb-1 text-[10px] border-orange-200 text-orange-700 bg-orange-50">{v.title}</Badge>
                                )) : <span className="text-xs text-neutral-400">-</span>}
                              </td>
                              <td className="p-4 text-center">
                                <span className={`text-sm font-bold ${threat.probability >= 4 ? 'text-red-600' : threat.probability >= 3 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {threat.probability}/5
                                </span>
                              </td>
                              <td className="p-4 text-center">
                                <span className={`text-sm font-bold ${avgImpact >= 4 ? 'text-red-600' : avgImpact >= 3 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {avgImpact}/5
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              ) : (
                <div className="py-8 text-center text-neutral-500">
                  <Filter className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-sm">Geen dreigingen gevonden met huidige filters</p>
                </div>
              );
            })()}

            {/* Org Threat Detail Right Panel */}
            <RightPanel
              open={!!detailOrgThreat}
              onClose={() => setDetailOrgThreat(null)}
              title={detailOrgThreat?.title || 'Dreiging'}
              subtitle={detailOrgThreat?.threat_id || ''}
              testId="org-threat-detail-panel"
              chain={
                <ChainVisual
                  activeStep="threat"
                  data={{
                    asset: detailOrgThreat?.linkedAssets?.length ? `${detailOrgThreat.linkedAssets.length} assets` : '',
                    vulnerability: detailOrgThreat?.linkedVulns?.length ? `${detailOrgThreat.linkedVulns.length} kwetsb.` : '',
                    threat: detailOrgThreat?.title || '',
                    risk: '',
                    measure: '',
                  }}
                  testId="threat-chain-visual"
                />
              }
              actions={
                <>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setDetailOrgThreat(null)}>Sluiten</Button>
                  <div className="flex-1" />
                  <Link to="/risks">
                    <Button size="sm" className="bg-black text-white text-xs">Bekijk Risico's</Button>
                  </Link>
                </>
              }
            >
              {detailOrgThreat && (
                <div className="space-y-5" data-testid="org-threat-detail-popup">
                  {detailOrgThreat.description && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Beschrijving</p>
                      <p className="text-sm text-neutral-700 leading-relaxed">{detailOrgThreat.description}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-neutral-50 rounded-lg text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Kans</p>
                      <p className={`text-lg font-bold ${detailOrgThreat.probability >= 4 ? 'text-red-600' : detailOrgThreat.probability >= 3 ? 'text-orange-600' : 'text-green-600'}`}>{detailOrgThreat.probability}/5</p>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">BIV Impact</p>
                      <p className={`text-lg font-bold ${detailOrgThreat.avgImpact >= 4 ? 'text-red-600' : detailOrgThreat.avgImpact >= 3 ? 'text-orange-600' : 'text-green-600'}`}>{detailOrgThreat.avgImpact}/5</p>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Risicoscore</p>
                      <p className="text-lg font-bold text-black">{detailOrgThreat.probability * detailOrgThreat.avgImpact}</p>
                    </div>
                  </div>
                  {detailOrgThreat.linkedAssets?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Gekoppelde Assets</p>
                      <div className="flex flex-wrap gap-1.5">
                        {detailOrgThreat.linkedAssets.map(a => (
                          <Badge key={a.id} variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50">{a.name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {detailOrgThreat.linkedVulns?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Gekoppelde Kwetsbaarheden</p>
                      <div className="flex flex-wrap gap-1.5">
                        {detailOrgThreat.linkedVulns.map(v => (
                          <Badge key={v.id} variant="outline" className="text-xs border-orange-200 text-orange-700 bg-orange-50">{v.title}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-neutral-100">
                    <div>
                      <p className="text-[10px] font-bold uppercase text-neutral-400 mb-1">Vertrouwelijkheid</p>
                      <p className="text-sm font-semibold text-black">{detailOrgThreat.impact_confidentiality || 3}/5</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-neutral-400 mb-1">Integriteit</p>
                      <p className="text-sm font-semibold text-black">{detailOrgThreat.impact_integrity || 3}/5</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-neutral-400 mb-1">Beschikbaarheid</p>
                      <p className="text-sm font-semibold text-black">{detailOrgThreat.impact_availability || 3}/5</p>
                    </div>
                  </div>

                  {/* DecisionRows — Guided Autonomy */}
                  <div className="space-y-1 pt-4 border-t border-neutral-100" data-testid="threat-decisions">
                    <DecisionRow
                      label="Relevantie"
                      value={detailOrgThreat.probability >= 3 ? 'Hoog, relevant voor jouw organisatie' : 'Laag, minder relevant'}
                      type="auto"
                      rationale={`Automatisch bepaald op basis van sector, profiel en waarschijnlijkheid (${detailOrgThreat.probability}/5)`}
                      confirmed={true}
                    />
                    {detailOrgThreat.linkedVulns?.length > 0 && (
                      <DecisionRow
                        label="Actie aanbevolen"
                        value={`${detailOrgThreat.linkedVulns.length} kwetsbaar${detailOrgThreat.linkedVulns.length !== 1 ? 'heden' : 'heid'} geraakt`}
                        type="recommended"
                        rationale="Deze dreiging raakt kwetsbaarheden in je systemen. Bekijk de bijbehorende risico's."
                        onConfirm={() => { setDetailOrgThreat(null); navigate('/risks'); }}
                        confirmLabel="Bekijk risico's"
                      />
                    )}
                  </div>
                </div>
              )}
            </RightPanel>

            {loadingVulns ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
              </div>
            ) : vulnerabilities.length > 0 ? (
              <>
                {/* Relevant threats based on vulnerability categories */}
                {(() => {
                  const vulnCategories = vulnerabilities.reduce((acc, v) => {
                    const cat = v.type || v.category || 'general';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(v);
                    return acc;
                  }, {});

                  const threatMapping = {
                    people: [
                      { title: 'Social engineering & phishing aanvallen', severity: 'high', description: 'Medewerkers zonder security awareness training zijn kwetsbaar voor gerichte phishing campagnes.' },
                      { title: 'Ongeautoriseerde toegang via gestolen credentials', severity: 'critical', description: 'Zwak wachtwoordbeleid en ontbreken van MFA verhoogt risico op account compromitatie.' },
                      { title: 'Insider threat door onvoldoende toegangscontrole', severity: 'medium', description: 'Zonder role-based access control kunnen medewerkers bij gevoelige data die buiten hun functie valt.' },
                    ],
                    process: [
                      { title: 'Dataverlies door ontbreken backup procedures', severity: 'high', description: 'Zonder getest backup- en herstelproces is uw organisatie kwetsbaar voor ransomware en dataverlies.' },
                      { title: 'Compliance schending door ontbrekende processen', severity: 'medium', description: 'Het ontbreken van gedocumenteerde processen kan leiden tot boetes bij audit.' },
                      { title: 'Supply chain aanval via leveranciers', severity: 'high', description: 'Onvoldoende leveranciersbeoordelingen kunnen leiden tot indirecte aanvallen via derden.' },
                    ],
                    technology: [
                      { title: 'Exploitatie van ongepatchte systemen', severity: 'critical', description: 'Verouderde software met bekende kwetsbaarheden kan actief misbruikt worden door aanvallers.' },
                      { title: 'Ransomware via onbeveiligde endpoints', severity: 'high', description: 'Endpoints zonder EDR-oplossing vormen een toegangspad voor ransomware-aanvallen.' },
                      { title: 'Datalek via misconfiguratie cloud services', severity: 'medium', description: 'Onjuist geconfigureerde cloud-omgevingen kunnen gevoelige data blootstellen.' },
                    ],
                    general: [
                      { title: 'Algemene cyberdreigingen voor uw sector', severity: 'medium', description: 'Op basis van uw bedrijfsprofiel zijn er generieke dreigingen die aandacht vereisen.' },
                    ]
                  };

                  return Object.entries(vulnCategories).map(([cat, vulns]) => {
                    const catLabel = cat === 'people' ? 'Mensen' : cat === 'process' ? 'Processen' : cat === 'technology' ? 'Technologie' : 'Algemeen';
                    const threats = threatMapping[cat] || threatMapping.general;

                    return (
                      <Card key={cat} className="border-neutral-200 shadow-sm" data-testid={`relevant-threats-${cat}`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-semibold text-black">
                              {catLabel}
                            </CardTitle>
                            <Badge className="bg-neutral-100 text-neutral-600">
                              {vulns.length} kwetsbaar{vulns.length !== 1 ? 'heden' : 'heid'}
                            </Badge>
                          </div>
                          <CardDescription className="text-xs text-neutral-500">
                            Bedreigingen gerelateerd aan {catLabel.toLowerCase()} kwetsbaarheden uit uw assessment
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {threats.map((threat, idx) => (
                            <div key={idx} className="p-4 rounded-lg bg-neutral-50 border border-neutral-100 cursor-pointer hover:border-neutral-200 hover:shadow-sm transition-all group" data-testid={`threat-item-${cat}-${idx}`}
                              onClick={(e) => {
                                const detail = e.currentTarget.querySelector('.threat-detail');
                                if (detail) detail.classList.toggle('hidden');
                              }}
                            >
                              <div className="flex items-start gap-6">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                threat.severity === 'critical' ? 'bg-red-100' :
                                threat.severity === 'high' ? 'bg-orange-100' : 'bg-yellow-100'
                              }`}>
                                <AlertTriangle className={`h-5 w-5 ${
                                  threat.severity === 'critical' ? 'text-red-600' :
                                  threat.severity === 'high' ? 'text-orange-600' : 'text-yellow-600'
                                }`} strokeWidth={1.5} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-sm font-semibold text-black">{threat.title}</h4>
                                  <Badge className={
                                    threat.severity === 'critical' ? 'bg-red-600 text-white' :
                                    threat.severity === 'high' ? 'bg-orange-500 text-white' :
                                    'bg-yellow-500 text-white'
                                  }>
                                    {threat.severity === 'critical' ? 'Kritiek' : threat.severity === 'high' ? 'Hoog' : 'Medium'}
                                  </Badge>
                                  <ChevronRight className="h-3.5 w-3.5 text-neutral-400 group-hover:text-neutral-600 transition-colors ml-auto" strokeWidth={1.5} />
                                </div>
                                <p className="text-xs text-neutral-500">{threat.description}</p>
                                <div className="threat-detail hidden mt-3 pt-3 border-t border-neutral-200 space-y-2">
                                  <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Aanbevolen mitigatie</p>
                                  <p className="text-xs text-neutral-600">Implementeer oplossingen gericht op {catLabel.toLowerCase()} om deze aanval te verminderen. Koppel deze aan je risicoregister via de Oplossingen-pagina.</p>
                                  <Button variant="outline" size="sm" className="text-xs h-7 border-neutral-200" onClick={(e) => { e.stopPropagation(); navigate('/measures'); }}>
                                    Bekijk oplossingen
                                  </Button>
                                </div>
                              </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  });
                })()}
              </>
            ) : (
              <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="threats-empty-state">
                <CardContent className="py-12 text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-neutral-100 mb-4">
                    <Target className="h-8 w-8 text-neutral-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-black mb-2">Nog geen aanvallen gekoppeld</h3>
                  <p className="text-sm text-neutral-500 max-w-md mx-auto mb-1">
                    Relevante aanvallen worden bepaald op basis van je systemen en zwakke plekken. Start de nulmeting om te beginnen.
                  </p>
                  <div className="flex items-center justify-center gap-3 mt-5">
                    <Link to="/assessment">
                      <Button className="bg-black text-white hover:bg-neutral-800" data-testid="threats-start-assessment">
                        Start nulmeting
                        <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                      </Button>
                    </Link>
                    <Link to="/assets">
                      <Button variant="outline" className="border-neutral-200" data-testid="threats-go-assets">
                        Systemen registreren
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Uitbreidingen Tab */}
        </Tabs>

            {/* NEXT STEP */}
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="threats-next-step">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="spk-section-label mb-0">Volgende stap</p>
                    {highThreats > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mt-1">Bekijk welke risico's deze dreigingen opleveren</h3>
                  <p className="text-sm text-neutral-600 mt-1">Dreigingen worden risico's als ze kwetsbaarheden raken. Bekijk en prioriteer je risico's.</p>
                </div>
                <Button
                  onClick={() => navigate('/risks')}
                  className="bg-black text-white hover:bg-neutral-800 shrink-0"
                  data-testid="threats-goto-risks"
                >
                  Bekijk risico's
                  <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                </Button>
              </div>
            </section>
          </div>
        </div>
    </PageShell>
  );
};

export default ThreatsPage;
